import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from './context/ThemeContext';

// Utils
import { API_BASE, fetchWithCredentials } from './utils/api';


// Components
import LoadingScreen from './components/Common/LoadingScreen';
import Notification from './components/Common/Notification';
import AchievementPopup from './components/Common/AchievementPopup';
import Header from './components/Layout/Header';
import PlayerStats from './components/Dashboard/PlayerStats';
import StatusBar from './components/Dashboard/StatusBar';
import TabNavigation from './components/Dashboard/TabNavigation';
import Marketplace from './components/Marketplace/Marketplace';
import Auctions from './components/Marketplace/Auctions';
import Transactions from './components/Marketplace/Transactions';
import Leaderboard from './components/Leaderboard/Leaderboard';

const RaikuSimulator = () => {
  const { theme } = useTheme();

  // State
  const [sessionId, setSessionId] = useState(null);
  const [currentSlot, setCurrentSlot] = useState(0);
  const [slots, setSlots] = useState([]);
  const [jitAuctions, setJitAuctions] = useState([]);
  const [aotAuctions, setAotAuctions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1, total_pages: 1, page_size: 20, total_count: 0, has_next: false, has_prev: false
  });
  const [activeTab, setActiveTab] = useState('marketplace');
  const [notifications, setNotifications] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [stats, setStats] = useState({
    active_jit_auctions: 0, active_aot_auctions: 0, total_transactions: 0
  });
  const [playerStats, setPlayerStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [achievementPopup, setAchievementPopup] = useState(null);
  const [achievementQueue, setAchievementQueue] = useState([]);
  const [isShowingAchievement, setIsShowingAchievement] = useState(false);

  // Refs
  const eventSourceRef = useRef(null);
  const notificationIdRef = useRef(0);
  const queuedAchievementIdsRef = useRef(new Set());
  const sessionCreatedRef = useRef(false);

  // Mobile check
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Notifications
  const addNotification = useCallback((message, type = 'info') => {
    const id = ++notificationIdRef.current;
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  }, []);

  // Achievements
  const queueAchievements = useCallback((achievements) => {
    const newAchievements = achievements.filter(
      achievement => !queuedAchievementIdsRef.current.has(achievement.achievement_type)
    );
    if (newAchievements.length > 0) {
      newAchievements.forEach(achievement => queuedAchievementIdsRef.current.add(achievement.achievement_type));
      setAchievementQueue(prev => [...prev, ...newAchievements]);
    }
  }, []);

  // Data Fetching
  const fetchPlayerStats = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await fetchWithCredentials(`${API_BASE}/game/player_stats?session_id=${sessionId}`);
      const responseData = await response.json();
      const data = responseData.data;
      setPlayerStats(prevStats => {
        if (prevStats && data.achievements.length > prevStats.achievements.length) {
          queueAchievements(data.achievements.slice(prevStats.achievements.length));
        }
        return data;
      });
    } catch (error) {
      console.error('Failed to fetch player stats:', error);
    }
  }, [sessionId, queueAchievements]);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetchWithCredentials(`${API_BASE}/game/leaderboard`);
      const responseData = await response.json();
      setLeaderboard(responseData.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  }, []);

  const fetchJitAuctions = useCallback(async () => {
    try {
      const response = await fetchWithCredentials(`${API_BASE}/auctions/jit`);
      const responseData = await response.json();
      setJitAuctions(responseData.data.auctions || []);
    } catch (error) {
      console.error('Failed to fetch JIT auctions:', error);
    }
  }, []);

  const fetchAotAuctions = useCallback(async () => {
    try {
      const response = await fetchWithCredentials(`${API_BASE}/auctions/aot`);
      const responseData = await response.json();
      setAotAuctions(responseData.data.auctions || []);
    } catch (error) {
      console.error('Failed to fetch AOT auctions:', error);
    }
  }, []);

  const fetchTransactions = useCallback(async (page = 1) => {
    if (!sessionId) return;
    try {
      const url = `${API_BASE}/transactions?session_id=${sessionId}&page=${page}&limit=20${showAllTransactions ? '&show_all=true' : ''}`;
      const response = await fetchWithCredentials(url);
      const responseData = await response.json();
      const data = responseData.data;
      setTransactions(data.transactions || []);
      setPagination(data.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, [sessionId, showAllTransactions]);

  // Event Handling
  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'SlotAdvanced': setCurrentSlot(event.current_slot); break;
      case 'SlotsUpdated': setSlots(event.slots); break;
      case 'JitAuctionStarted':
        fetchJitAuctions();
        addNotification(`JIT auction started for slot ${event.slot_number}`, 'info');
        break;
      case 'AotAuctionStarted':
        fetchAotAuctions();
        addNotification(`AOT auction started for slot ${event.slot_number}`, 'info');
        break;
      case 'JitBidSubmitted':
        fetchJitAuctions();
        addNotification(`JIT bid submitted: ${event.amount} SOL`, 'success');
        break;
      case 'AotBidSubmitted':
        fetchAotAuctions();
        addNotification(`AOT bid submitted: ${event.amount} SOL`, 'success');
        break;
      case 'JitAuctionResolved':
        fetchJitAuctions();
        if (event.winner === sessionId) addNotification(`JIT auction won! Slot ${event.slot_number}: ${event.winning_bid} SOL`, 'success');
        fetchPlayerStats();
        fetchLeaderboard();
        break;
      case 'AotAuctionResolved':
        fetchAotAuctions();
        if (event.winner === sessionId) addNotification(`AOT auction won! Slot ${event.slot_number}: ${event.winning_bid} SOL`, 'success');
        fetchPlayerStats();
        fetchLeaderboard();
        break;
      case 'TransactionUpdated':
        if (sessionId && event.transaction.sender === sessionId) fetchTransactions();
        break;
      case 'MarketplaceStats':
        setStats({
          active_jit_auctions: event.active_jit_auctions,
          active_aot_auctions: event.active_aot_auctions,
          total_transactions: event.total_transactions
        });
        setCurrentSlot(event.current_slot);
        break;
      default: break;
    }
  }, [sessionId, addNotification, fetchAotAuctions, fetchJitAuctions, fetchLeaderboard, fetchPlayerStats, fetchTransactions]);

  const handleEventRef = useRef(null);
  useEffect(() => {
    handleEventRef.current = handleEvent;
  }, [handleEvent]);

  const connectEventSource = useCallback(() => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    const eventSource = new EventSource(`${API_BASE}/events`);
    eventSourceRef.current = eventSource;
    eventSource.onopen = () => {
      setConnectionStatus('connected');
      addNotification('Real-time connection established!', 'success');
    };
    eventSource.onmessage = (event) => {
      try {
        if (handleEventRef.current) {
          handleEventRef.current(JSON.parse(event.data));
        }
      } catch (error) {
        console.error('Failed to parse event data:', error);
      }
    };
    eventSource.onerror = () => {
      setConnectionStatus('error');
      setTimeout(() => { if (sessionId) connectEventSource(); }, 3000);
    };
    return eventSource;
  }, [sessionId, addNotification]); // Removed handleEvent from dependencies

  const createSession = useCallback(async () => {
    if (sessionCreatedRef.current) return;
    sessionCreatedRef.current = true;
    try {
      const response = await fetchWithCredentials(`${API_BASE}/sessions`, { method: 'POST' });
      const responseData = await response.json();
      setSessionId(responseData.data.session_id);
      addNotification(responseData.data.status === 'validated' ? 'Session restored!' : 'New session created!', 'success');
    } catch (error) {
      addNotification('Failed to create session. Please refresh.', 'error');
      sessionCreatedRef.current = false;
    }
  }, [addNotification]);

  const fetchInitialData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const [statusRes, slotsRes, jitRes, aotRes, txRes] = await Promise.all([
        fetchWithCredentials(`${API_BASE}/marketplace/status`),
        fetchWithCredentials(`${API_BASE}/marketplace/slots`),
        fetchWithCredentials(`${API_BASE}/auctions/jit`),
        fetchWithCredentials(`${API_BASE}/auctions/aot`),
        fetchWithCredentials(`${API_BASE}/transactions?session_id=${sessionId}`)
      ]);
      const [status, slotsData, jitData, aotData, txData] = await Promise.all([statusRes.json(), slotsRes.json(), jitRes.json(), aotRes.json(), txRes.json()]);
      setCurrentSlot(status.current_slot);
      setSlots(slotsData.slots || []);
      setJitAuctions(jitData.auctions || []);
      setAotAuctions(aotData.auctions || []);
      setTransactions(txData.transactions || []);
      fetchPlayerStats();
      fetchLeaderboard();
    } catch (err) {
      console.error('Initial fetch error:', err);
    }
  }, [sessionId, fetchPlayerStats, fetchLeaderboard]);

  useEffect(() => { createSession(); }, [createSession]);
  useEffect(() => {
    if (sessionId) {
      fetchInitialData();
      const es = connectEventSource();
      return () => es.close();
    }
  }, [sessionId, fetchInitialData, connectEventSource]);

  useEffect(() => {
    if (activeTab === 'transactions' && sessionId) fetchTransactions(1);
  }, [activeTab, sessionId, showAllTransactions, fetchTransactions]);

  useEffect(() => {
    if (!isShowingAchievement && achievementQueue.length > 0) {
      const next = achievementQueue[0];
      setAchievementPopup(next);
      setIsShowingAchievement(true);
      addNotification(`🏆 ${next.name}!`, 'success');
      setTimeout(() => {
        setAchievementPopup(null);
        setIsShowingAchievement(false);
        setAchievementQueue(prev => prev.slice(1));
      }, 2000);
    }
  }, [isShowingAchievement, achievementQueue, addNotification]);

  // Actions
  const submitJitBid = async () => {
    if (!playerStats) return addNotification('Loading player stats...', 'error');
    const input = prompt(`Enter JIT bid amount (Balance: ${playerStats.balance.toFixed(3)} SOL):`);
    if (!input) return;
    const bidAmount = parseFloat(input);
    if (isNaN(bidAmount) || bidAmount <= 0) return addNotification('Invalid bid!', 'error');
    if (bidAmount > playerStats.balance) return addNotification('Insufficient balance!', 'error');

    try {
      const res = await fetchWithCredentials(`${API_BASE}/transactions/jit`, {
        method: 'POST', body: JSON.stringify({ session_id: sessionId, bid_amount: bidAmount, compute_units: 200000, data: 'JIT transaction' })
      });
      if (!res.ok && res.status === 402) addNotification('Insufficient balance!', 'error');
    } catch (err) { addNotification('JIT bid failed', 'error'); }
  };

  const submitAotBid = async (slotNumber) => {
    if (!playerStats) return addNotification('Loading player stats...', 'error');
    const input = prompt(`Enter AOT bid amount (Balance: ${playerStats.balance.toFixed(3)} SOL):`);
    if (!input) return;
    const bidAmount = parseFloat(input);
    if (isNaN(bidAmount) || bidAmount <= 0) return addNotification('Invalid bid!', 'error');
    if (bidAmount > playerStats.balance) return addNotification('Insufficient balance!', 'error');

    try {
      const res = await fetchWithCredentials(`${API_BASE}/transactions/aot`, {
        method: 'POST', body: JSON.stringify({ session_id: sessionId, slot_number: slotNumber, bid_amount: bidAmount, compute_units: 200000, data: 'AOT transaction' })
      });
      if (!res.ok && res.status === 402) addNotification('Insufficient balance!', 'error');
    } catch (err) { addNotification('AOT bid failed', 'error'); }
  };

  const tabs = [
    { id: 'marketplace', name: 'Marketplace' },
    { id: 'auctions', name: 'Auctions' },
    { id: 'transactions', name: 'Transactions' },
    { id: 'leaderboard', name: 'Leaderboard' },
  ];

  if (!sessionId) return <LoadingScreen isMobile={isMobile} />;

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: theme.bg, fontFamily: 'monospace',
      padding: isMobile ? '12px' : '17px', animation: 'fadeIn 0.5s ease-in-out', transition: 'background-color 0.4s ease'
    }}>
      <AchievementPopup achievement={achievementPopup} isMobile={isMobile} />
      <Notification notifications={notifications} isMobile={isMobile} />

      <Header connectionStatus={connectionStatus} isMobile={isMobile} />

      <PlayerStats playerStats={playerStats} isMobile={isMobile} />

      <StatusBar
        currentSlot={currentSlot}
        stats={stats}
        sessionId={sessionId}
        connectionStatus={connectionStatus}
        isMobile={isMobile}
      />

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        dropdownOpen={dropdownOpen}
        setDropdownOpen={setDropdownOpen}
        handleTabChange={(id) => { setActiveTab(id); setDropdownOpen(false); }}
        isMobile={isMobile}
      />

      <div style={{ padding: isMobile ? '0' : '0 5%' }}>
        {activeTab === 'marketplace' && (
          <Marketplace
            slots={slots}
            currentSlot={currentSlot}
            submitAotBid={submitAotBid}
            submitJitBid={submitJitBid}
            playerStats={playerStats}
            isMobile={isMobile}
          />
        )}
        {activeTab === 'auctions' && (
          <Auctions jitAuctions={jitAuctions} aotAuctions={aotAuctions} isMobile={isMobile} />
        )}
        {activeTab === 'transactions' && (
          <Transactions
            transactions={transactions}
            showAllTransactions={showAllTransactions}
            toggleTransactionView={() => { setShowAllTransactions(!showAllTransactions); setCurrentPage(1); }}
            pagination={pagination}
            currentPage={currentPage}
            handlePageChange={(p) => fetchTransactions(p)}
            sessionId={sessionId}
            isMobile={isMobile}
          />
        )}
        {activeTab === 'leaderboard' && (
          <Leaderboard leaderboard={leaderboard} sessionId={sessionId} isMobile={isMobile} />
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bubbleAppear { from { transform: scale(0.1); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes achievementPop { from { transform: translate(-50%, -50%) scale(0.5); opacity: 0; } to { transform: translate(-50%, -50%) scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default RaikuSimulator;